import { useCallback } from "react";
import { useNavigate } from "react-router";
import { APP_REGISTRY, useIsOsMode, useOsActionsOptional, useWindowId } from "@/components/ui/os-layout";
import { parseUrlToWindowConfig } from "@/components/ui/os-layout/urlMapping";
import { buildTaskReviewPath, type ReviewOpenFocus, type ReviewOpenMode } from "@/components/review/reviewOpenMode";

export type OpenTaskReviewOptions = {
  focus?: ReviewOpenFocus;
  mode?: ReviewOpenMode;
  /** Navigate within the current review surface instead of opening a new window. */
  inPlace?: boolean;
};

export { buildTaskReviewPath };

/** Open the Review OS for a task — OS window or classic route. */
export function useOpenTaskReview(): (taskId: string, title?: string, options?: OpenTaskReviewOptions) => void {
  const isOsMode = useIsOsMode();
  const actionsContext = useOsActionsOptional();
  const windowId = useWindowId();
  const navigate = useNavigate();

  return useCallback(
    (taskId: string, title?: string, options?: OpenTaskReviewOptions) => {
      const path = buildTaskReviewPath(taskId, options);
      const windowTitle =
        options?.mode === "progress"
          ? title?.trim() || "Onboarding progress"
          : title?.trim() || "Review";

      if (options?.inPlace) {
        const windowConfig = parseUrlToWindowConfig(path);
        if (
          isOsMode &&
          windowId &&
          actionsContext?.updateWindow &&
          windowConfig?.appId === "task-review"
        ) {
          actionsContext.updateWindow(windowId, {
            params: windowConfig.params,
            title: windowTitle,
          });
          return;
        }

        navigate(path);
        return;
      }

      if (!isOsMode || !actionsContext?.openWindow) {
        navigate(path);
        return;
      }

      const windowConfig = parseUrlToWindowConfig(path);
      if (!windowConfig) {
        navigate(path);
        return;
      }

      const appDef = APP_REGISTRY[windowConfig.appId];
      if (!appDef) {
        navigate(path);
        return;
      }

      actionsContext.openWindow({
        appId: windowConfig.appId,
        title: windowTitle,
        params: windowConfig.params,
        initialSize: appDef.defaultSize,
      });
    },
    [actionsContext, isOsMode, navigate, windowId],
  );
}
